import styled, { css, keyframes } from 'styled-components';
import { TILE_SIZE_PX } from './AttachmentTray.constants';

export const TrayList = styled.div<{ $align: 'start' | 'end' }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.level3};
  justify-content: ${({ $align }) => ($align === 'end' ? 'flex-end' : 'flex-start')};
`;

const interactive = css`
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const ThumbnailTile = styled.button<{ $interactive: boolean }>`
  position: relative;
  width: ${TILE_SIZE_PX}px;
  height: ${TILE_SIZE_PX}px;
  padding: 0;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surfaceContainerHighest};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $interactive }) => ($interactive ? interactive : css`cursor: default;`)}
`;

/** The glyph a square tile shows in place of a thumbnail it cannot render. */
export const TileGlyph = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  /* Clear of the caption, so the glyph reads as centred in what is left. */
  padding-bottom: 18px;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.7;

  > svg {
    width: 30px;
    height: 30px;
  }
`;

export const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ThumbnailCaption = styled.span`
  position: absolute;
  inset: auto 0 0 0;
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level2}
    ${({ theme }) => theme.spacing.level2};
  ${({ theme }) => theme.font.labelSmall};
  color: #fff;
  background: linear-gradient(to top, rgb(0 0 0 / 78%), rgb(0 0 0 / 0%));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`;

export const UnavailableSlide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.level6};
  ${({ theme }) => theme.font.bodyMedium};
  color: #fff;
`;

export const FileChip = styled.button<{ $interactive: boolean; $shrink?: boolean }>`
  display: inline-flex;
  align-items: center;
  min-width: ${({ $shrink }) => ($shrink ? '0' : 'auto')};
  gap: ${({ theme }) => theme.spacing.level3};
  max-width: 280px;
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level4};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surfaceContainerHighest};
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurface};
  text-align: left;
  overflow: hidden;

  > svg:first-child {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
    opacity: 0.7;
  }

  ${({ $interactive }) => ($interactive ? interactive : css`cursor: default;`)}
`;

export const FileChipName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

export const FileChipSize = styled.span`
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.7;
  flex-shrink: 0;
`;

export const TileWrapper = styled.div`
  position: relative;
  display: inline-flex;

  &:hover > button[data-remove] {
    opacity: 1;
  }
`;

export const RemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 1;
  width: 20px;
  height: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.surfaceContainerHighest};
  color: ${({ theme }) => theme.colors.onSurface};
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;

  &:focus-visible {
    opacity: 1;
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.7; }
`;

export const ThumbnailSkeleton = styled.span`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  animation: ${pulse} 1.2s ease-in-out infinite;
`;
